import React, { useState, useRef, useEffect, useCallback } from 'react';
import { focusAfterRowAction } from '../../utils/focusAfterRowAction';
import { useNavigate, Link } from 'react-router-dom';
import {
  RiBellLine,
  RiBellFill,
  RiCloseLine,
  RiCheckLine,
  RiDeleteBinLine,
  RiSettings3Line,
  RiInformationLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiAlertLine,
  RiRefreshLine,
  RiLoader4Line
} from 'react-icons/ri';
import { NotificationMessage } from '../../hooks/useWebSocket';
import notificationService from '../../services/notificationService';
import { usePermission } from '../../hooks/usePermission';
import { resolveNotificationTarget } from '../../utils/notificationRoutes';

export interface NotificationBellProps {
  notifications: NotificationMessage[];
  unreadCount: number;
  onMarkAsRead: (notificationId: string) => void;
  onMarkAllAsRead: () => void;
  onClearAll: () => void;
  onRemove: (notificationId: string) => void;
  onSettingsClick?: () => void;
  settingsHref?: string;
  canViewAllNotifications?: boolean;
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  notifications: wsNotifications,
  unreadCount: wsUnreadCount,
  onMarkAsRead,
  onMarkAllAsRead,
  onClearAll,
  onRemove,
  onSettingsClick,
  settingsHref,
  canViewAllNotifications = true
}) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellButtonRef = useRef<HTMLButtonElement>(null);
  const panelCloseButtonRef = useRef<HTMLButtonElement>(null);

  // KD3 S2566 (NFR-NOTIF-003): ปุ่มอ่านแล้ว/ลบ/อ่านทั้งหมด/ลบทั้งหมด หายจาก DOM ทันทีหลังกด
  // → โฟกัสคีย์บอร์ดหล่นไป body · ย้ายโฟกัสไปปุ่มที่ยังอยู่ก่อนเปลี่ยน state (fallback = ปุ่มปิด panel)
  const keepFocusInPanel = (e: React.MouseEvent<HTMLElement>, removesRow: boolean) => {
    focusAfterRowAction(e.currentTarget, {
      rowSelector: '[data-notification-row]',
      removesRow,
      fallback: panelCloseButtonRef.current,
    });
  };
  const [dbNotifications, setDbNotifications] = useState<NotificationMessage[]>([]);
  const [dbUnreadCount, setDbUnreadCount] = useState(0);
  const [loadingDb, setLoadingDb] = useState(false);

  // Fetch notifications from API (DB) when dropdown opens
  const fetchFromApi = useCallback(async () => {
    setLoadingDb(true);
    try {
      const response = await notificationService.getNotifications({ limit: 20, unread_only: false });
      const items = response?.items || (response as any)?.data?.items || [];
      const apiUnreadCount = response?.unread_count ?? (response as any)?.data?.unread_count;
      const mapped: NotificationMessage[] = items.map((n: any) => {
        // Map backend type to frontend display type
        let displayType: 'info' | 'success' | 'warning' | 'error' = 'info';
        // NEG-NOTIF-920: type ใหม่ที่แยกออกมาเพื่อ gate สิทธิ์ ต้องคงระดับความรุนแรงเดิมไว้
        // (เดิมส่งมาเป็น alert/warning ล้วน) ไม่งั้นเตือน 'เลยกำหนดคืน' จะกลายเป็นสีข้อมูลธรรมดา
        if (['maintenance_new', 'warning', 'contract_expiring', 'contract_terminated', 'payment_overdue',
             'invoice_due_soon', 'invoice_overdue', 'sim_expiring', 'sim_expired',
             'rental_due', 'rental_overdue'].includes(n.type)) displayType = 'warning';
        else if (['error'].includes(n.type)) displayType = 'error';
        else if (['success', 'maintenance_completed'].includes(n.type)) displayType = 'success';

        // Map category from type
        let category: 'system' | 'rental' | 'contract' | 'payment' | 'maintenance' = 'system';
        if (n.type?.includes('maintenance') || n.reference_type === 'maintenance') category = 'maintenance';
        else if (n.type?.includes('contract') || n.reference_type === 'contract') category = 'contract';
        else if (n.type?.includes('rental') || n.reference_type === 'rental') category = 'rental';
        else if (n.type?.includes('payment') || n.type?.includes('invoice') || n.reference_type === 'payment') category = 'payment';

        return {
          id: String(n.id),
          type: displayType,
          title: n.title || '',
          message: n.message || '',
          timestamp: n.created_at || new Date().toISOString(),
          read: n.is_read || false,
          category,
          reference_type: n.reference_type || n.data?.reference_type || null,
          reference_id: n.reference_id || n.data?.reference_id || n.data?.maintenance_id || null,
          data: n.data || null,
        };
      });
      setDbNotifications(mapped);
      setDbUnreadCount(typeof apiUnreadCount === 'number' ? apiUnreadCount : mapped.filter(n => !n.read).length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoadingDb(false);
    }
  }, []);

  // Fetch on mount + when dropdown opens
  useEffect(() => { fetchFromApi(); }, [fetchFromApi]);
  useEffect(() => { if (isOpen) fetchFromApi(); }, [isOpen, fetchFromApi]);

  // Re-fetch when notifications are changed elsewhere (e.g. read on the
  // /notifications page) so the header badge stays in sync without a route change.
  useEffect(() => {
    const handler = () => fetchFromApi();
    window.addEventListener('notifications-updated', handler);
    return () => window.removeEventListener('notifications-updated', handler);
  }, [fetchFromApi]);

  // Merge: WS notifications + DB notifications (deduplicate by ID)
  const allNotifications = React.useMemo(() => {
    const seen = new Set<string>();
    const merged: NotificationMessage[] = [];
    for (const n of wsNotifications) {
      seen.add(n.id);
      merged.push(n);
    }
    for (const n of dbNotifications) {
      if (!seen.has(n.id)) {
        merged.push(n);
      }
    }
    return merged.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [wsNotifications, dbNotifications]);

  // P-S461-1: collapse การแจ้งเตือนเรื่องเดียวกัน (entity เดียวกัน + หัวข้อเดียวกัน) เป็นแถวเดียว
  // — เดิม follow-up ซ้ำ 3 ใบติดกันดัน notification อื่นตกจอ · ตัวแทน = ใบใหม่สุด (list sort desc แล้ว)
  type GroupedNotification = NotificationMessage & {
    groupCount: number;
    groupIds: string[];
    groupUnreadIds: string[];
  };
  const groupedNotifications = React.useMemo<GroupedNotification[]>(() => {
    const groups = new Map<string, GroupedNotification>();
    for (const n of allNotifications) {
      const key = (n.reference_type && n.reference_id != null)
        ? `${n.reference_type}:${n.reference_id}:${n.title}`
        : `title:${n.title}:${n.message}`;
      const g = groups.get(key);
      if (!g) {
        groups.set(key, { ...n, groupCount: 1, groupIds: [n.id], groupUnreadIds: n.read ? [] : [n.id] });
      } else {
        g.groupCount++;
        g.groupIds.push(n.id);
        if (!n.read) {
          g.groupUnreadIds.push(n.id);
          g.read = false; // group ยัง unread ถ้ามีสมาชิกยังไม่อ่าน
        }
      }
    }
    return [...groups.values()];
  }, [allNotifications]);

  const notifications = groupedNotifications;
  const unreadCount = Math.max(wsUnreadCount, dbUnreadCount);

  // Wrap mark-as-read handlers to also update local dbUnreadCount immediately
  const handleMarkAsRead = useCallback((notificationId: string) => {
    onMarkAsRead(notificationId);
    setDbUnreadCount(prev => Math.max(0, prev - 1));
    setDbNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
  }, [onMarkAsRead]);

  const handleMarkAllAsRead = useCallback(() => {
    onMarkAllAsRead();
    setDbUnreadCount(0);
    setDbNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, [onMarkAllAsRead]);

  // BUG-S1368 FIX: ปุ่มลบต้องอัพเดต dbNotifications (state ที่ดึงจาก API) ด้วย —
  // เดิมแตะแค่ WS list ใน context รายการจาก DB จึงค้างบนจอทั้งที่กดลบ/ยืนยันแล้ว
  const handleRemove = useCallback((ids: string[]) => {
    ids.forEach((id) => onRemove(id));
    const idSet = new Set(ids);
    // KD3 S2570 (NEG-NOTIF-003): ห้ามเรียก setDbUnreadCount ภายใน updater ของ setDbNotifications —
    // React เรียก updater ซ้ำได้ (StrictMode/การ re-run) ทำให้ badge ลดทีละ 2 ต่อการลบ 1 ใบ
    // (ลบ 5 ใบ badge 15→5 ขณะ DB เหลือ 10) · นับจาก state ปัจจุบันแล้วค่อยสั่ง set แยกกัน
    const removedUnread = dbNotifications.filter(n => idSet.has(n.id) && !n.read).length;
    setDbNotifications(prev => prev.filter(n => !idSet.has(n.id)));
    if (removedUnread > 0) {
      setDbUnreadCount(c => Math.max(0, c - removedUnread));
    }
  }, [onRemove, dbNotifications]);

  const handleClearAll = useCallback(() => {
    onClearAll();
    setDbNotifications([]);
    setDbUnreadCount(0);
  }, [onClearAll]);

  // Navigate to related page when clicking a notification item
  const handleNotificationClick = useCallback((notification: GroupedNotification) => {
    // Mark as read first — P-S461-1: แถวที่ collapse แล้วต้อง mark ทุกใบใน group ไม่ให้ลูก unread ค้าง
    const unreadIds = notification.groupUnreadIds?.length
      ? notification.groupUnreadIds
      : (!notification.read ? [notification.id] : []);
    unreadIds.forEach((id) => handleMarkAsRead(id));

    // Build target URL based on reference_type + reference_id
    const refType = notification.reference_type || notification.data?.reference_type;
    const refId = notification.reference_id || notification.data?.reference_id || notification.data?.maintenance_id;

    let targetUrl: string | null = null;
    let targetPermission: string | null = null;

    // Map reference_type → {path, permission ที่ route เป้าหมายใช้ gate (ตรงกับ App.tsx)}
    // ตารางกลาง utils/notificationRoutes.ts ใช้ร่วมกับหน้า /notifications (B-01: เดิมสองไฟล์ถือคนละชุด
    // และไม่มี quotation / preparation_job) · G-01: invoice/payment ไม่มี route รายตัว → ไปหน้า list
    const referenceTarget = resolveNotificationTarget(refType, refId || null);
    if (referenceTarget) {
      targetUrl = referenceTarget.path;
      targetPermission = referenceTarget.permission;
    }

    // Fallback: navigate to category page
    if (!targetUrl) {
      const categoryRouteMap: Record<string, { path: string; permission: string }> = {
        'maintenance': { path: '/maintenance/list', permission: 'maintenance.view' },
        'rental': { path: '/rentals/list', permission: 'rentals.view' },
        'contract': { path: '/contracts/list', permission: 'contracts.view' },
        'payment': { path: '/billing/invoices', permission: 'invoices.view' },
        'system': { path: '/notifications', permission: 'notifications.view' },
      };
      const entry = categoryRouteMap[notification.category];
      targetUrl = entry?.path || '/notifications';
      targetPermission = entry?.permission || null;
    }

    // G-16: ผู้ใช้ไม่มีสิทธิ์ดู route เป้าหมาย → ไปหน้ารวมการแจ้งเตือนแทน
    // (/notifications gate ด้วย notifications.view ซึ่งผู้ใช้ที่เห็นกระดิ่งมีอยู่แล้ว)
    if (targetPermission && !hasPermission(targetPermission)) {
      targetUrl = '/notifications';
    }

    // Close dropdown and navigate
    setIsOpen(false);
    navigate(targetUrl);
  }, [handleMarkAsRead, navigate, hasPermission]);

  // ปิด dropdown เมื่อคลิกข้างนอก + กด Escape (BUG-A11Y-NOTI-002: keyboard users
  // ต้องปิด dropdown ได้ด้วย Escape ไม่ใช่แค่ mouse outside-click — WCAG 2.1.1;
  // mirror ของ BUG-A11Y-ADROP-001 fix [d002c43] ที่ ActionDropdown)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        bellButtonRef.current?.focus(); // คืน focus กลับที่กระดิ่ง (WCAG 2.4.3)
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // ฟังก์ชันสำหรับแสดงไอคอนตามประเภทการแจ้งเตือน
  const getNotificationIcon = (type: NotificationMessage['type']) => {
    switch (type) {
      case 'success':
        return <RiCheckboxCircleLine className="w-5 h-5 text-success-500" />;
      case 'warning':
        return <RiAlertLine className="w-5 h-5 text-warning-500" />;
      case 'error':
        return <RiErrorWarningLine className="w-5 h-5 text-danger-500" />;
      default:
        return <RiInformationLine className="w-5 h-5 text-info-500" />;
    }
  };

  // ฟังก์ชันสำหรับแสดงสีพื้นหลังตามประเภท
  const getNotificationBg = (type: NotificationMessage['type'], read: boolean) => {
    // KD3 S2566 (NFR-NOTIF-004): เดิมใส่ opacity-50 ทั้งแถวที่อ่านแล้ว → ตัวอักษรทุกตัวจางจน contrast
    // เหลือ 1.5–2.6:1 (axe color-contrast serious · WCAG 1.4.3 ต้อง ≥4.5:1)
    // ใช้พื้นขาว + เส้นข้างสีกลางแทน (สีตัวอักษรของแถวอ่านแล้วสลับเป็น secondary-600/500 อยู่แล้ว)
    if (read) return 'bg-white border-secondary-200';

    switch (type) {
      case 'success':
        return 'bg-success-50 border-success-200';
      case 'warning':
        return 'bg-warning-50 border-warning-200';
      case 'error':
        return 'bg-danger-50 border-danger-200';
      default:
        return 'bg-info-50 border-info-200';
    }
  };

  // ฟังก์ชันสำหรับแปลงประเภทการแจ้งเตือนเป็นภาษาไทย
  const getCategoryLabel = (category: NotificationMessage['category']) => {
    switch (category) {
      case 'rental':
        return 'การเช่า';
      case 'contract':
        return 'สัญญา';
      case 'payment':
        return 'การชำระเงิน';
      case 'maintenance':
        return 'การบำรุงรักษา';
      case 'system':
      default:
        return 'ระบบ';
    }
  };

  // ฟังก์ชันสำหรับจัดรูปแบบเวลา
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'เมื่อสักครู่';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} นาทีที่แล้ว`;
    } else if (diffHours < 24) {
      return `${diffHours} ชั่วโมงที่แล้ว`;
    } else if (diffDays < 7) {
      return `${diffDays} วันที่แล้ว`;
    } else {
      return new Intl.DateTimeFormat('th-TH', {
        day: 'numeric',
        month: 'short',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        ref={bellButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 text-secondary-600 hover:text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center"
        aria-label={unreadCount > 0 ? `การแจ้งเตือน — ${unreadCount} รายการที่ยังไม่อ่าน` : 'การแจ้งเตือน'}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {unreadCount > 0 ? (
          <RiBellFill className="w-6 h-6 text-primary-600" />
        ) : (
          <RiBellLine className="w-6 h-6" />
        )}
        
        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-danger-700 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div
          className="fixed inset-x-2 top-16 sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 w-auto sm:w-96 bg-white rounded-lg shadow-xl border border-secondary-200 z-50 max-h-[32rem] overflow-hidden"
          role="region"
          aria-labelledby="notification-panel-heading"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-secondary-200 bg-secondary-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <h3 id="notification-panel-heading" className="text-lg font-semibold text-secondary-900">การแจ้งเตือน</h3>
                {unreadCount > 0 && (
                  <span className="bg-danger-700 text-white text-xs px-2 py-1 rounded-full font-medium">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-1">
                {unreadCount > 0 && (
                  <button
                    onClick={(e) => { keepFocusInPanel(e, false); handleMarkAllAsRead(); }}
                    className="text-xs text-primary-600 hover:text-primary-800 font-medium px-2 py-1 rounded hover:bg-primary-50"
                    title="ทำเครื่องหมายว่าอ่านทั้งหมด"
                  >
                    อ่านทั้งหมด
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={() => {
                      // ลบจริงถึงฐานข้อมูล (ไม่ใช่แค่ล้างหน้าจอ) — ยืนยันก่อนกัน mis-click
                      if (window.confirm('ต้องการลบการแจ้งเตือนทั้งหมดใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้')) {
                        panelCloseButtonRef.current?.focus();
                        handleClearAll();
                      }
                    }}
                    className="p-2.5 sm:p-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center text-secondary-500 hover:text-danger-600 hover:bg-danger-50 rounded"
                    title="ลบทั้งหมด"
                    aria-label="ลบทั้งหมด"
                  >
                    <RiDeleteBinLine className="w-4 h-4" />
                  </button>
                )}
                {(settingsHref || onSettingsClick) && (
                  settingsHref ? (
                    <Link
                      to={settingsHref}
                      className="p-2.5 sm:p-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded"
                      title="ตั้งค่าการแจ้งเตือน"
                      aria-label="ตั้งค่าการแจ้งเตือน"
                    >
                      <RiSettings3Line className="w-4 h-4" />
                    </Link>
                  ) : (
                    <button
                      onClick={onSettingsClick}
                      className="p-2.5 sm:p-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded"
                      title="ตั้งค่าการแจ้งเตือน"
                      aria-label="ตั้งค่าการแจ้งเตือน"
                    >
                      <RiSettings3Line className="w-4 h-4" />
                    </button>
                  )
                )}
                <button
                  ref={panelCloseButtonRef}
                  onClick={() => setIsOpen(false)}
                  className="p-2.5 sm:p-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded"
                  title="ปิด"
                  aria-label="ปิดการแจ้งเตือน"
                >
                  <RiCloseLine className="w-4 h-4" />
                </button>
              </div>
            </div>
            {unreadCount > 0 && (
              <p className="text-sm text-secondary-600 mt-1">
                มีการแจ้งเตือนใหม่ {unreadCount} รายการ
              </p>
            )}
          </div>

          {/* Notification List */}
          <div
            className="max-h-80 overflow-y-auto"
            role="log"
            aria-live="polite"
            aria-relevant="additions"
            aria-label="รายการการแจ้งเตือน"
          >
            {loadingDb && notifications.length === 0 ? (
              <div className="px-6 py-12 text-center text-secondary-500">
                <RiLoader4Line className="w-10 h-10 mx-auto mb-3 text-primary-400 animate-spin" />
                <p className="text-sm text-secondary-500">กำลังโหลดการแจ้งเตือน...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-6 py-12 text-center text-secondary-500">
                <RiBellLine className="w-16 h-16 mx-auto mb-4 text-secondary-300" />
                <h4 className="text-lg font-medium text-secondary-900 mb-2">ไม่มีการแจ้งเตือน</h4>
                <p className="text-sm text-gray-500">
                  เมื่อมีการแจ้งเตือนใหม่ จะแสดงที่นี่
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    data-notification-row
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-secondary-50 transition-colors duration-150 border-l-4 cursor-pointer ${getNotificationBg(notification.type, notification.read)}`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className={`text-sm font-medium min-w-0 break-words ${notification.read ? 'text-secondary-600' : 'text-secondary-900'}`}>
                                {notification.title}
                              </h4>
                              {!notification.read && (
                                <span className="inline-block w-2 h-2 bg-primary-500 rounded-full"></span>
                              )}
                              {notification.groupCount > 1 && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary-700 flex-shrink-0"
                                  title={`มีการแจ้งเตือนเรื่องเดียวกัน ${notification.groupCount} รายการ`}
                                >
                                  ×{notification.groupCount}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                                {getCategoryLabel(notification.category)}
                              </span>
                              <span className="text-xs text-secondary-600">
                                {formatTime(notification.timestamp)}
                              </span>
                            </div>
                            {/* KD3 S2573 (NEG-NOTIF-014): ข้อความยาว (เช่น 2,000 ตัวอักษรจาก K-Visit/cron) เคยแสดงเต็มใน
                                dropdown → แถวเดียวสูง >1,000px กลบใบอื่นในลิสต์ max-h 320 · ตัดเหลือ 3 บรรทัด (เห็นเต็มใน modal/หน้า /notifications) */}
                            <p
                              className={`text-sm leading-relaxed line-clamp-3 break-words ${notification.read ? 'text-secondary-500' : 'text-secondary-700'}`}
                              title={notification.message.length > 160 ? notification.message : undefined}
                            >
                              {notification.message}
                            </p>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center space-x-1 ml-3 flex-shrink-0">
                            {!notification.read && (
                              <button
                                onClick={(e) => { e.stopPropagation(); keepFocusInPanel(e, false); notification.groupUnreadIds.forEach((id) => handleMarkAsRead(id)); }}
                                className="p-2.5 sm:p-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center text-secondary-400 hover:text-success-600 hover:bg-success-50 rounded transition-colors"
                                title="ทำเครื่องหมายว่าอ่านแล้ว"
                                aria-label="ทำเครื่องหมายว่าอ่านแล้ว"
                              >
                                <RiCheckLine className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); keepFocusInPanel(e, true); handleRemove(notification.groupIds); }}
                              className="p-2.5 sm:p-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center text-secondary-400 hover:text-danger-600 hover:bg-danger-50 rounded transition-colors"
                              title="ลบการแจ้งเตือน"
                              aria-label="ลบการแจ้งเตือน"
                            >
                              <RiCloseLine className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer - แสดงเฉพาะเมื่อมี notifications และมีสิทธิ์ดูหน้า notifications */}
          {notifications.length > 0 && canViewAllNotifications && (
            <div className="px-4 py-3 border-t border-secondary-200 bg-secondary-50">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notifications');
                }}
                className="w-full text-center text-sm text-primary-600 hover:text-primary-800 hover:bg-primary-50 font-medium py-2 px-4 rounded transition-colors"
              >
                ดูการแจ้งเตือนทั้งหมด
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;